import { api } from "@/modules/api";

import { subscribeDesktopRealtime } from "./desktop-event-gateway";
import { ParsedDesktopEvent } from "./desktop-event-parser";
import { DesktopAudioAssembler } from "./desktop-audio-assembler";
import { DesktopTranscriptAssembler } from "./desktop-transcript-assembler";

import { electronModule } from "@/modules/electron";
import type { TimestampedAssemblyAITurnMessage } from "@/domains/dexie-db/audio-capture/types";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

export type DesktopCaptureCallbacks = {
  onTranscript: (channel: 0 | 1, turn: TimestampedAssemblyAITurnMessage) => void;
  onPcm?: (samples: Int16Array) => void;
};

export class DesktopCaptureService {
  private windowId: string | null = null;
  private uploadToken: string | null = null;
  private unsubscribe: (() => void) | null = null;
  private assembler = new DesktopAudioAssembler();
  private transcriptAssembler = new DesktopTranscriptAssembler();
  private stoppingPromise: Promise<void> | null = null;
  private activeWindowId: string | null = null;

  private callbacks: DesktopCaptureCallbacks;

  constructor(callbacks: DesktopCaptureCallbacks) {
    this.callbacks = callbacks;
  }

  async prepare(noteId: string, capturedAudioId: string): Promise<void> {
    this.transcriptAssembler.reset();
    const { windowId } = await electronModule.recall.prepareDesktopRecording();
    this.windowId = windowId;
    this.activeWindowId = windowId;

    const response = await api.post("/v2/voice/recall_sdk_upload", {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      body: { note_id: noteId, captured_audio_id: capturedAudioId } as any,
    });
    const uploadToken = response.data?.upload_token as string | undefined;
    if (!uploadToken) throw new Error("Failed to get desktop capture upload token");
    this.uploadToken = uploadToken;

    this.unsubscribe = subscribeDesktopRealtime(event => this.onEvent(event));
  }

  async start(): Promise<void> {
    if (!this.windowId || !this.uploadToken) throw new Error("Desktop capture not prepared");
    await electronModule.recall.startRecording({
      windowId: this.windowId,
      uploadToken: this.uploadToken,
    });
  }

  async pause(): Promise<void> {
    if (!this.windowId) return;
    await electronModule.recall.pause({ windowId: this.windowId });
  }

  async resume(): Promise<void> {
    if (!this.windowId) return;
    await electronModule.recall.resume({ windowId: this.windowId });
  }

  async stop(): Promise<Blob> {
    await this.ensureSdkStopped();
    const blob = await this.assembler.finalizeWav();
    this.cleanupAfterSession();
    return blob;
  }

  async cancel(): Promise<void> {
    await this.ensureSdkStopped();
    this.cleanupAfterSession();
  }

  dispose() {
    void this.cancel();
  }

  private async ensureSdkStopped(): Promise<void> {
    if (!this.windowId) {
      return;
    }
    if (this.stoppingPromise) {
      await this.stoppingPromise;
      return;
    }

    const windowId = this.windowId;
    this.stoppingPromise = (async () => {
      try {
        await electronModule.recall.stop({ windowId });
      } catch (err) {
        logger.warn({
          message: "[DesktopCaptureService] stop failed",
          info: { error: objectModule.safeErrorAsJson(err as Error) },
        });
      } finally {
        if (this.windowId === windowId) {
          this.windowId = null;
          this.activeWindowId = null;
        }
        this.uploadToken = null;
        this.stoppingPromise = null;
      }
    })();
    await this.stoppingPromise;
  }

  private cleanupAfterSession() {
    try {
      this.unsubscribe?.();
    } catch (_) {
      /* empty */
    }
    this.unsubscribe = null;
    this.assembler.reset();
    this.transcriptAssembler.reset();
    this.windowId = null;
    this.uploadToken = null;
    this.activeWindowId = null;
  }

  flushPendingPcmBlob(): Blob | null {
    return this.assembler.flushPendingToPcmBlob();
  }

  getPendingPcmBlob(): Blob | null {
    return this.assembler.getPendingPcmBlob();
  }

  private onEvent(event: ParsedDesktopEvent) {
    if (!event) return;

    if (event.type === "audio_mixed_raw.data") {
      // Recall AI includes the originating windowId, but we currently allow only one
      // recording at a time. Capture any audio payload the SDK emits.
      const samples = this.assembler.appendBase64Pcm16(event.bufferBase64);
      this.callbacks.onPcm?.(samples);
      return;
    }
    const channel: 0 | 1 = event.speakerIsHost ? 0 : 1; // host → Me (0), participants → Other Speakers (1)
    const update = this.transcriptAssembler.ingest({ channel, event });
    if (update) {
      this.callbacks.onTranscript(update.channel, update.turn);
    }
  }
}
